import {
  Users,
  Settings,
  HelpCircle,
  Activity,
  LayoutDashboard,
  MessageSquare,
  BarChart3,
  ChevronDown,
  Bot,
  BookOpen,
  Plug,
  ArrowRightLeft,
  Wrench,
  Import,
  CreditCard,
  SlidersHorizontal,
  Library,
  Gauge,
  Compass,
  Megaphone,
  KeyRound,
  Sparkles,
  Wand2,
  UserCog,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UserSidebarMenu } from "./user-sidebar-menu";
import { CollapsibleMenuItem } from "./collapsible-menu-item";
import { Link, useLocation } from "@tanstack/react-router";

const items = [
  {
    title: "Overview",
    url: "/",
    icon: LayoutDashboard,
  },
  {
    title: "Tenants",
    url: "/tenants",
    icon: Users,
  },
  {
    title: "Health Check",
    url: "/health",
    icon: Activity,
  },
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
  },
  {
    title: "Help",
    url: "/help",
    icon: HelpCircle,
  },
];

const conversationsEnabled =
  import.meta.env.VITE_FEATURE_CONVERSATIONS === "true";

export function AppSidebar() {
  const location = useLocation();
  const sidebar = useSidebar();

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className="flex flex-row items-center">
            <SidebarMenuButton
              size="lg"
              className="hover:bg-transparent active:bg-transparent"
              asChild
            >
              <div
                className="cursor-pointer select-none"
                onClick={() => {
                  if (!sidebar.open) {
                    sidebar.setOpen(true);
                  }
                }}
              >
                <div className="flex aspect-square size-8 items-center justify-center rounded-lg">
                  <img
                    src="/android-chrome-512x512.png"
                    alt="Harmony"
                    className="size-8"
                  />
                </div>
                {sidebar.open && (
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      Harmony Management
                    </span>
                    <span className="truncate text-xs">
                      Tenant Management Suite
                    </span>
                  </div>
                )}
              </div>
            </SidebarMenuButton>
            {sidebar.open && <SidebarTrigger />}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Overview */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={location.pathname === "/"}>
                  <Link to="/">
                    <LayoutDashboard />
                    <span>Overview</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Tenants */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.startsWith("/tenants")}
                >
                  <Link to="/tenants">
                    <Users />
                    <span>Tenants</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Integrations */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.startsWith("/integrations")}
                >
                  <Link to="/integrations">
                    <Plug />
                    <span>Integrations</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Billing */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={location.pathname.startsWith("/billing")}
                >
                  <Link to="/billing">
                    <CreditCard />
                    <span>Billing</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>

              {/* Backoffice Tools */}
              <CollapsibleMenuItem
                trigger={
                  <SidebarMenuButton title="Backoffice Tools">
                    <Wrench />
                    <span>Backoffice Tools</span>
                    <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                  </SidebarMenuButton>
                }
                hoverContent={
                  <>
                    <Link
                      to="/solution-engineer"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <UserCog className="size-4 shrink-0" />
                      <span className="truncate">Solution Engineer</span>
                    </Link>
                    <Link
                      to="/desk-migration"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <ArrowRightLeft className="size-4 shrink-0" />
                      <span className="truncate">Desk Migration</span>
                    </Link>
                    <Link
                      to="/desk-suggestions"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <Sparkles className="size-4 shrink-0" />
                      <span className="truncate">Desk Suggestions</span>
                    </Link>
                    <Link
                      to="/desk-improvements"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <Wand2 className="size-4 shrink-0" />
                      <span className="truncate">Improve Desks</span>
                    </Link>
                    <Link
                      to="/itsm-import"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <Import className="size-4 shrink-0" />
                      <span className="truncate">ITSM Import</span>
                    </Link>
                    <Link
                      to="/itsm-config"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <SlidersHorizontal className="size-4 shrink-0" />
                      <span className="truncate">ITSM Config</span>
                    </Link>
                    <Link
                      to="/kb-config"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <Library className="size-4 shrink-0" />
                      <span className="truncate">KB Config</span>
                    </Link>
                    <Link
                      to="/intent-hub"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <Compass className="size-4 shrink-0" />
                      <span className="truncate">Intents</span>
                    </Link>
                    <Link
                      to="/broadcast"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <Megaphone className="size-4 shrink-0" />
                      <span className="truncate">Broadcast</span>
                    </Link>
                    <Link
                      to="/access-keys"
                      className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                    >
                      <KeyRound className="size-4 shrink-0" />
                      <span className="truncate">API Keys</span>
                    </Link>
                  </>
                }
                inlineContent={
                  <SidebarMenuSub>
                    <SidebarMenuSubItem>
                      <SidebarMenuSubButton
                        asChild
                        title="Solution Engineer"
                        className="!h-8"
                      >
                        <Link
                          to="/solution-engineer"
                          activeProps={{ "data-active": "true" }}
                        >
                          <UserCog className="size-4" />
                          <span>Solution Engineer</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="Desk Migration"
                        className="!h-8"
                      >
                        <Link
                          to="/desk-migration"
                          activeProps={{ "data-active": "true" }}
                        >
                          <ArrowRightLeft className="size-4" />
                          <span>Desk Migration</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="Desk Suggestions"
                        className="!h-8"
                      >
                        <Link
                          to="/desk-suggestions"
                          activeProps={{ "data-active": "true" }}
                        >
                          <Sparkles className="size-4" />
                          <span>Desk Suggestions</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="Improve Desks"
                        className="!h-8"
                      >
                        <Link
                          to="/desk-improvements"
                          activeProps={{ "data-active": "true" }}
                        >
                          <Wand2 className="size-4" />
                          <span>Improve Desks</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="ITSM Import"
                        className="!h-8"
                      >
                        <Link
                          to="/itsm-import"
                          activeProps={{ "data-active": "true" }}
                        >
                          <Import className="size-4" />
                          <span>ITSM Import</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="ITSM Config"
                        className="!h-8"
                      >
                        <Link
                          to="/itsm-config"
                          activeProps={{ "data-active": "true" }}
                        >
                          <SlidersHorizontal className="size-4" />
                          <span>ITSM Config</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="KB Config"
                        className="!h-8"
                      >
                        <Link
                          to="/kb-config"
                          activeProps={{ "data-active": "true" }}
                        >
                          <Library className="size-4" />
                          <span>KB Config</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="Intents"
                        className="!h-8"
                      >
                        <Link
                          to="/intent-hub"
                          activeProps={{ "data-active": "true" }}
                        >
                          <Compass className="size-4" />
                          <span>Intents</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="Broadcast"
                        className="!h-8"
                      >
                        <Link
                          to="/broadcast"
                          activeProps={{ "data-active": "true" }}
                        >
                          <Megaphone className="size-4" />
                          <span>Broadcast</span>
                        </Link>
                      </SidebarMenuSubButton>
                      <SidebarMenuSubButton
                        asChild
                        title="API Keys"
                        className="!h-8"
                      >
                        <Link
                          to="/access-keys"
                          activeProps={{ "data-active": "true" }}
                        >
                          <KeyRound className="size-4" />
                          <span>API Keys</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  </SidebarMenuSub>
                }
              />

              {/* Agent (collapsible) */}
              {conversationsEnabled ? (
                <CollapsibleMenuItem
                  trigger={
                    <SidebarMenuButton title="Agent">
                      <Bot />
                      <span>Agent</span>
                      <ChevronDown className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-180" />
                    </SidebarMenuButton>
                  }
                  hoverContent={
                    <>
                      <Link
                        to="/agent/conversations"
                        className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                      >
                        <MessageSquare className="size-4 shrink-0" />
                        <span className="truncate">Conversations</span>
                      </Link>
                      <Link
                        to="/agent/analytics"
                        className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                      >
                        <BarChart3 className="size-4 shrink-0" />
                        <span className="truncate">Analytics</span>
                      </Link>
                      <Link
                        to="/agent/kb-insights"
                        className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                      >
                        <BookOpen className="size-4 shrink-0" />
                        <span className="truncate">Insights</span>
                      </Link>
                      <Link
                        to="/agent/ai-deflection-map"
                        className="ring-sidebar-ring outline-hidden hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground flex h-8 w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm transition-[width,height,padding] focus-visible:ring-2"
                      >
                        <Gauge className="size-4 shrink-0" />
                        <span className="truncate">AI Deflection</span>
                      </Link>
                    </>
                  }
                  inlineContent={
                    <SidebarMenuSub>
                      <SidebarMenuSubItem>
                        <SidebarMenuSubButton
                          asChild
                          title="Conversations"
                          className="!h-8"
                        >
                          <Link
                            to="/agent/conversations"
                            activeProps={{ "data-active": "true" }}
                          >
                            <MessageSquare className="size-4" />
                            <span>Conversations</span>
                          </Link>
                        </SidebarMenuSubButton>
                        <SidebarMenuSubButton
                          asChild
                          title="Analytics"
                          className="!h-8"
                        >
                          <Link
                            to="/agent/analytics"
                            activeProps={{ "data-active": "true" }}
                          >
                            <BarChart3 className="size-4" />
                            <span>Analytics</span>
                          </Link>
                        </SidebarMenuSubButton>
                        <SidebarMenuSubButton
                          asChild
                          title="Insights"
                          className="!h-8"
                        >
                          <Link
                            to="/agent/kb-insights"
                            activeProps={{ "data-active": "true" }}
                          >
                            <BookOpen className="size-4" />
                            <span>Insights</span>
                          </Link>
                        </SidebarMenuSubButton>
                        <SidebarMenuSubButton
                          asChild
                          title="AI Deflection"
                          className="!h-8"
                        >
                          <Link
                            to="/agent/ai-deflection-map"
                            activeProps={{ "data-active": "true" }}
                          >
                            <Gauge className="size-4" />
                            <span>AI Deflection</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    </SidebarMenuSub>
                  }
                />
              ) : (
                <SidebarMenuItem>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-full">
                        <SidebarMenuButton
                          disabled
                          className="w-full cursor-not-allowed opacity-50"
                        >
                          <Bot />
                          <span>Agent</span>
                        </SidebarMenuButton>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      <p>Only available in production</p>
                    </TooltipContent>
                  </Tooltip>
                </SidebarMenuItem>
              )}

              {/* Remaining items */}
              {items.slice(2).map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={
                      item.url === "/"
                        ? location.pathname === "/"
                        : location.pathname.startsWith(item.url)
                    }
                  >
                    <Link to={item.url}>
                      <item.icon />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <UserSidebarMenu />
      </SidebarFooter>
    </Sidebar>
  );
}
